import {
  Button,
  FlexBox,
  Icon,
  Page,
  Spinner,
  Table,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { useCallback, useEffect, useState } from 'react';

import { knowledgeBasesApi, WebsiteKnowledgeBase } from 'apis/knoledgeBases';
import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { DataSourceTableRow } from './components/dataSourceTableRow';

export const DataSourceWrapper = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const notify = useNotifications();

  const { id } = useAppPathParams('/settings/chatbots/data-source/:id');

  const { dataSource, isLoading, isError } =
    knowledgeBasesApi.useReadKnowledgeBaseByIdQuery(
      { id },
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ data, ...rest }) => {
          return {
            ...rest,
            dataSource: data?.data as WebsiteKnowledgeBase,
          };
        },
      },
    );

  const [updateWebsiteDataSource, { isLoading: isUpdating }] =
    knowledgeBasesApi.useUpdateKnowledgeBaseMutation();

  const [syncDataSource] = knowledgeBasesApi.useSyncKnowledgeBaseMutation();

  const [isPristine, setIsPristine] = useState(true);

  const inProgress = isLoading || isUpdating;

  const handleSendFeedback = useCallback(() => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'app:feedbackForm',
          arg: {
            title: 'Chatbot Feedback',
            team: 'automation',
          },
        },
      }),
    );
  }, []);

  const [urls, setUrls] = useState(dataSource?.custom_settings.urls ?? []);
  const [excludedUrls, setExcludedUrls] = useState(
    dataSource?.custom_settings.excluded_urls ?? [],
  );

  useEffect(() => {
    setUrls(dataSource?.custom_settings.urls ?? []);
    setExcludedUrls(dataSource?.custom_settings.excluded_urls ?? []);
  }, [dataSource]);

  const exclude = useCallback((url: string, index: number) => {
    setExcludedUrls((prevExcludedUrls) => [...prevExcludedUrls, url]);
    setUrls((prevUrls) => prevUrls.filter((_, i) => i !== index));
    setIsPristine(false);
  }, []);

  const include = useCallback((url: string, index: number) => {
    setUrls((prevUrls) => [...prevUrls, url]);
    setExcludedUrls((prevExcludedUrls) =>
      prevExcludedUrls.filter((_, i) => i !== index),
    );
    setIsPristine(false);
  }, []);

  const handleSyncDataSources = () => {
    syncDataSource({ id });
  };

  const handleUpdateDataSources = async () => {
    try {
      const response = await updateWebsiteDataSource({
        id,
        type: 'website',
        identity: {
          name: dataSource?.name,
          excluded_urls: excludedUrls,
        },
      }).unwrap();

      if (isResponseWithData(response)) {
        notify(
          {
            title: 'Success',
            text: `"${response.data?.name}" has been updated.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );
        setIsPristine(true);
      }
    } catch (error) {
      notify(
        {
          title: 'Error while updating website data source.',
          icon: 'exclamation',
          className: 'danger',
          time: 5000,
        },
        NotificationType.Error,
      );
    }
  };

  return (
    <>
      <Page>
        <Page.Head
          title={
            <FlexBox gap="200" alignItems="center">
              <AppLink
                path="/settings/chatbots/mim"
                style={{ color: 'inherit' }}
              >
                Mim chatbots
              </AppLink>
              <Icon name="angle-right" />
              Data sources
              <Icon name="angle-right" />
              {dataSource?.name}
              <Icon name="pen" size={16} />
              <Text.ExternalLink onClick={handleSendFeedback} fontSize="200">
                Send feedback
              </Text.ExternalLink>
            </FlexBox>
          }
          controls={
            <>
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  navigate({ path: '/settings/chatbots/mim' });
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isPristine || inProgress}
                isLoading={inProgress}
                loadingIndicator={<Spinner size={16} />}
                onClick={handleUpdateDataSources}
              >
                Save changes
              </Button>
            </>
          }
        />

        <Page.Body>
          <Page>
            <Page.Section
              title="URLs"
              titleMeta={
                <Tooltip
                  content="We sync our web sources daily. Changes to URLs may take up to 1 day to appear in chatbot responses.
You can manually re-sync your URLs if you know the content has changed."
                >
                  <Icon name="info" />
                </Tooltip>
              }
              controls={
                <Button
                  variant="secondary"
                  slotStart={<Icon name="sync" />}
                  onClick={handleSyncDataSources}
                >
                  Re-sync all URLs
                </Button>
              }
            >
              <Table
                stickyHeader
                loading={isLoading}
                loadingFailed={isError}
                emptyState={
                  dataSource?.state === 'syncing'
                    ? {
                        title: 'URLs are being crawled',
                        description:
                          'We are currently adding all the URLs from your website. They will appear here shortly. Feel free to navigate away from this page; the process will continue in the background.',
                      }
                    : undefined
                }
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th columnId="url" width="90%">
                      URL
                    </Table.Th>
                    <Table.Th columnId="active" width="10%">
                      Active
                    </Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>

                <Table.Tbody>
                  {urls.map((url, index) => (
                    <DataSourceTableRow
                      key={index}
                      url={url}
                      index={index}
                      isExcluded={false}
                      onToggle={exclude}
                    />
                  ))}
                  {excludedUrls.map((url, index) => (
                    <DataSourceTableRow
                      key={index}
                      url={url}
                      index={index}
                      isExcluded={true}
                      onToggle={include}
                    />
                  ))}
                </Table.Tbody>
              </Table>
            </Page.Section>
          </Page>
        </Page.Body>
      </Page>
    </>
  );
};
